import ArticleSelector from '@/components/selectors/ArticleSelector';
import {
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { useMemo } from 'react';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import ContractDatasourceSelector from './ContractDatasourceSelector';
import ContractTimeFrameSelector from './ContractTimeFrameSelector';
import areIdentifiersEqual from './areIdentifiersEqual';
import { v4 as uuidv4 } from 'uuid';
import ContractTimeSelector from './ContractTimeSelector';
import { useRouteLoaderData } from 'react-router-dom';
import { useState } from 'react';
import { useEffect } from 'react';
import { fetchWeclappPrice } from './fetchWeclappPrice';
import { useTheme } from '@emotion/react';
import DraggablePaper from '@/components/DraggablePaper';
import HoldButton from '@/components/HoldButton';

const ContentEditDialog = ({
  editingItemUuid,
  handleCloseDialog,
  formState,
  setFormState,
  tempRange,
  setTempRange,
  sourceOptions,
  handleSave,
  contractItems,
  customerId,
  isSalesBOMItem = false,
  groupNames,
  deleteItem,
}) => {
  const theme = useTheme();
  const {
    fixedDeploymentConfigs: { contracts },
  } = useRouteLoaderData('auth');
  const [isPriceLoading, setIsPriceLoading] = useState(false);

  const { canEnableSplit, splitTooltipMessage } = useMemo(() => {
    let canEnable = false;
    let tooltipMessage = 'Ein Aufteilen ist für diese Position nicht möglich.';

    if (formState?.type === 'DYNAMIC') {
      const items = contractItems || [];
      const currentIndex =
        editingItemUuid !== 'new'
          ? items.findIndex((item) => item.uuid === formState.uuid)
          : items.length;
      const identifiers = formState.quantity?.identifiers;

      const nextItemInChain = items
        .slice(currentIndex + 1)
        .some(
          (futureItem) =>
            futureItem.type === 'DYNAMIC' &&
            areIdentifiersEqual(futureItem.quantity?.identifiers, identifiers),
        );
      const previousItem = items
        .slice(0, currentIndex)
        .reverse()
        .find(
          (prevItem) =>
            prevItem.type === 'DYNAMIC' &&
            areIdentifiersEqual(prevItem.quantity?.identifiers, identifiers),
        );

      const canJoinContinuingGroup =
        previousItem?.quantity?.split && previousItem?.quantity?.max != null;

      if (canJoinContinuingGroup) {
        canEnable = true;
        tooltipMessage = 'Aufteilen um Position in der Gruppe aufzunehmen.';
      } else if (nextItemInChain || editingItemUuid === 'new') {
        if (formState.quantity?.max != null) {
          canEnable = true;
          tooltipMessage = 'Erstellen einer aufgeteilten Gruppe';
        } else {
          tooltipMessage =
            'Um diese Position aufzuteilen, muss eine Höchstmenge definiert sein.';
        }
      } else {
        tooltipMessage =
          'Dies ist die einzige/letzte Position für diese Quelle. Ein Aufteilen ist nicht möglich.';
      }
    }
    return { canEnableSplit: canEnable, splitTooltipMessage: tooltipMessage };
  }, [formState, editingItemUuid, contractItems]);

  const selectedSourceOptions = useMemo(() => {
    const sourceIdentifiers = formState?.quantity?.identifiers;
    if (!Array.isArray(sourceIdentifiers) || !sourceOptions) return [];
    return sourceOptions.filter((opt) =>
      sourceIdentifiers.includes(opt.articleNumber),
    );
  }, [formState, sourceOptions]);

  const sourceNotFound = useMemo(() => {
    if (formState?.type !== 'DYNAMIC') return false; //  && !formState?.quantity?.identifiers
    const sourceIdentifiers = formState?.quantity?.identifiers;
    if (!Array.isArray(sourceIdentifiers) || sourceIdentifiers.length === 0)
      return false;

    const availableArticleNumbers = new Set(
      sourceOptions.map((opt) => opt.articleNumber),
    );
    return sourceIdentifiers.some((id) => !availableArticleNumbers.has(id));
  }, [formState, sourceOptions]);

  useEffect(() => {
    const handler = setTimeout(() => {
      const fetchPrice = async () => {
        if (!formState?.articleId || !customerId) {
          return;
        }

        setIsPriceLoading(true);

        const quantityForScale =
          formState.type === 'STATIC'
            ? formState.quantity || 0
            : formState.quantity?.min || 0;

        const pricingDate = formState.pricingDate
          ? new Date(formState.pricingDate)
          : new Date();

        const { price: newPrice, discountPercentage } = await fetchWeclappPrice(
          {
            articleId: formState.articleId,
            customerId: customerId,
            pricingDate: pricingDate,
            quantity: quantityForScale,
            weclappApiUrl: contracts?.interfaces?.weclapp,
          },
        );

        setIsPriceLoading(false);

        if (newPrice !== null) {
          setFormState((prevState) => ({
            ...prevState,
            unitPrice: newPrice,
            unchangedUnitPrice: newPrice,
            discountPercentage:
              formState?.discountPercentage &&
              formState?.discountPercentage !== 0
                ? formState.discountPercentage
                : discountPercentage,
          }));
        }
      };

      fetchPrice();
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [
    formState?.articleId,
    formState?.pricingDate,
    customerId,
    contracts,
    setFormState,
    formState?.quantity,
    formState?.type,
  ]);

  const selectedGroupValue = useMemo(() => {
    if (!formState.groupName) return null;
    return groupNames.find((g) => g.uuid === formState.groupName) || null;
  }, [groupNames, formState.groupName]);

  return (
    <Dialog
      open={true}
      onClose={handleCloseDialog}
      maxWidth="md"
      fullWidth
      PaperComponent={DraggablePaper}
      aria-labelledby="draggable-dialog-title"
    >
      <DialogTitle
        id="draggable-dialog-title"
        className="draggable-handle"
        sx={{
          cursor: 'move',
          userSelect: 'none',
          backgroundColor: 'background.paper',
        }}
      >
        {editingItemUuid === 'new'
          ? 'Neue Position erstellen'
          : isSalesBOMItem
            ? 'Unterposition bearbeiten'
            : 'Position bearbeiten'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ flex: 1, pt: 1 }}>
          <ArticleSelector
            article={
              formState?.articleId
                ? {
                    id: formState.articleId,
                    name: formState.title,
                    articleNumber: formState.articleNumber,
                  }
                : null
            }
            setArticle={(article) => {
              setFormState((prevState) => ({
                ...prevState,
                uuid: uuidv4(),
                articleId: article?.id,
                articleNumber: article?.articleNumber,
                title: article?.name,
                description: article?.description || article?.name,
                unitPrice: article?.salesPrice || 0,
                articleFactor: article?.factor || 1,
                factor: 1,
                discountPercentage: 0,
                useSalesBillOfMaterialItemPrices:
                  article?.useSalesBillOfMaterialItemPrices ?? true,
                articleType: article?.articleType || 'BASIC',
                ...(article?.articleType === 'SALES_BILL_OF_MATERIAL' && {
                  salesBillOfMaterialItems: (
                    article.salesBillOfMaterialItems || []
                  ).map((sub) => ({
                    ...sub,
                    articleFactor: sub.factor || 1,
                  })),
                }),
              }));
            }}
            enrichData={true}
          />
          {!isSalesBOMItem && (
            <>
              <Box sx={{ mt: 2 }}>
                <Autocomplete
                  options={groupNames}
                  getOptionLabel={(option) => option.title || ''}
                  isOptionEqualToValue={(option, value) =>
                    option.uuid === value.uuid
                  }
                  value={selectedGroupValue}
                  onChange={(event, newValue) => {
                    setFormState({
                      ...formState,
                      groupName: newValue ? newValue.uuid : null,
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Gruppe"
                      variant="outlined"
                      size="small"
                      placeholder="Gruppe auswählen"
                    />
                  )}
                  noOptionsText="Keine Gruppen im Vertrag definiert. Bitte im Vertragskopf bearbeiten."
                />
              </Box>
              <ContractTimeFrameSelector
                formState={formState}
                setFormState={setFormState}
                tempRange={tempRange}
                setTempRange={setTempRange}
                theme={theme}
              />
            </>
          )}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              size="small"
              label="Preis/Einh."
              type="number"
              value={formState.unitPrice || ''}
              sx={{ mt: 2, flex: 1 }}
              InputProps={{
                endAdornment: isPriceLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <InputAdornment position="start">€</InputAdornment>
                ),
              }}
              disabled={true}
            />
            <Box sx={{ flex: 1, height: '100%' }}>
              <ContractTimeSelector
                label="Preisstellungsdatum"
                date={formState.pricingDate}
                onDateChange={(newDate) => {
                  setFormState({
                    ...formState,
                    pricingDate: newDate,
                    ...(formState?.salesBillOfMaterialItems?.length > 0 && {
                      salesBillOfMaterialItems:
                        formState.salesBillOfMaterialItems.map((item) => ({
                          ...item,
                          pricingDate: newDate,
                        })),
                    }),
                  });
                }}
                height={39.5}
                pt={0.1}
                mt={2}
                theme={theme}
                variant="outlined"
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField
              size="small"
              label="Rabatt %"
              type="number"
              value={formState.discountPercentage ?? ''}
              onChange={(e) =>
                setFormState({
                  ...formState,
                  discountPercentage: Number(e.target.value),
                })
              }
              sx={{ flex: 1 }}
            />
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Rundung</InputLabel>
              <Select
                value={formState.rounding}
                label="Rundung"
                onChange={(e) =>
                  setFormState({ ...formState, rounding: e.target.value })
                }
              >
                <MenuItem value="EXACT">Exakter Wert</MenuItem>
                <MenuItem value="CEIL">Aufrunden</MenuItem>
                <MenuItem value="ROUND">Kaufmännisch (0.5)</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <ToggleButtonGroup
            sx={{ mt: 2 }}
            fullWidth
            value={formState.type}
            exclusive
            onChange={(e, newValue) => {
              if (newValue !== null) {
                setFormState({ ...formState, type: newValue });
              }
            }}
          >
            <ToggleButton value="STATIC">Statisch</ToggleButton>
            <ToggleButton value="DYNAMIC">Dynamisch</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {formState.type === 'STATIC' ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              mt: 2,
            }}
          >
            <TextField
              label="Menge"
              type="number"
              fullWidth
              value={formState.quantity || ''}
              onChange={(e) =>
                setFormState({
                  ...formState,
                  quantity: Number(e.target.value),
                })
              }
              disabled={isSalesBOMItem}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              mt: 2,
            }}
          >
            <ContractDatasourceSelector
              selectedSourceOptions={selectedSourceOptions}
              sourceOptions={sourceOptions}
              setFormState={setFormState}
              formState={formState}
              sourceNotFound={sourceNotFound}
            />
            {!isSalesBOMItem && (
              <>
                <TextField
                  size="small"
                  label="Mindestmenge"
                  type="number"
                  inputProps={{ min: 0 }}
                  fullWidth
                  value={formState.quantity?.min ?? ''}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      quantity: {
                        ...formState.quantity,
                        min:
                          e.target.value === ''
                            ? undefined
                            : Number(e.target.value),
                      },
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Höchstmenge"
                  type="number"
                  inputProps={{ min: 0 }}
                  fullWidth
                  value={formState.quantity?.max ?? ''}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      quantity: {
                        ...formState.quantity,
                        max:
                          e.target.value === ''
                            ? undefined
                            : Number(e.target.value),
                      },
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Faktor (Multiplikator)"
                  type="number"
                  value={formState.factor ?? 1}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      factor: Number(e.target.value),
                    })
                  }
                  sx={{ flex: 1 }}
                  inputProps={{ step: '0.1' }}
                />
                <Tooltip title={splitTooltipMessage}>
                  <span>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!formState.quantity?.split}
                          onChange={(e) =>
                            setFormState({
                              ...formState,
                              quantity: {
                                ...formState.quantity,
                                split: e.target.checked,
                              },
                            })
                          }
                          disabled={!canEnableSplit}
                        />
                      }
                      label="Menge aufteilen"
                    />
                  </span>
                </Tooltip>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!isSalesBOMItem && (
          <>
            <HoldButton
              countDown={2}
              countMessage="Bitte halten Sie die Taste 2 Sekunden gedrückt"
              variant="outlined"
              color="error"
              onClick={() => deleteItem(editingItemUuid)}
            >
              Löschen
            </HoldButton>

            <Box sx={{ flexGrow: 1 }} />
          </>
        )}
        <Button onClick={handleCloseDialog} color="inherit">
          Abbrechen
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContentEditDialog;
